import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from './api';

export interface User {
  id: string;
  email: string;
  tenantId: string;
  permissions: string[];
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  tenantId: string | null;
  token: string | null;
  loading: boolean;
  setTenantId: (id: string) => void;
  login: (email: string, password: string) => Promise<{ mfaRequired?: boolean; challengeToken?: string }>;
  register: (email: string, password: string, workspaceName: string) => Promise<void>;
  mfaChallenge: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      tenantId: null,
      token: null,
      loading: true,

      setTenantId: (id: string) => set({ tenantId: id }),

      async login(email, password) {
        const res = await api.post<{
          token?: string;
          tenantId?: string;
          userId?: string;
          mfaRequired?: boolean;
          challengeToken?: string;
        }>('/auth/login', { email, password });

        if (res.mfaRequired) {
          return { mfaRequired: true, challengeToken: res.challengeToken };
        }

        if (res.tenantId && res.token) {
          set({ tenantId: res.tenantId, token: res.token });
          await get().checkSession();
        }
        return {};
      },

      async register(email, password, workspaceName) {
        const res = await api.post<{ tenantId: string; userId: string; token: string }>('/auth/register', {
          email,
          password,
          workspaceName,
        });
        set({ tenantId: res.tenantId, token: res.token });
        await get().checkSession();
      },

      async mfaChallenge(challengeToken, code) {
        const res = await api.post<{ tenantId: string; userId: string; token: string }>('/auth/mfa/challenge', {
          challengeToken,
          code,
        });
        set({ tenantId: res.tenantId, token: res.token });
        await get().checkSession();
      },

      async logout() {
        try { await api.post('/auth/logout'); } catch {}
        set({ user: null, tenant: null, tenantId: null, token: null });
      },

      async checkSession() {
        const { tenantId, token } = get();
        if (!tenantId || !token) {
          set({ loading: false, user: null, tenant: null });
          return;
        }
        try {
          const me = await api.get<{ user: User; tenant: Tenant }>('/auth/me');
          set({ user: me.user, tenant: me.tenant, loading: false });
        } catch {
          // Invalid/expired session — clear and bounce to login
          set({ user: null, tenant: null, tenantId: null, token: null, loading: false });
        }
      },
    }),
    {
      name: 'frontdesk-auth',
      partialize: (s) => ({ tenantId: s.tenantId, token: s.token }),
    }
  )
);
